'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import Image from 'next/image';
import { format } from 'date-fns';

import type {
  HousingAvailabilityDay,
  HousingPropertyDetail,
} from '@eliadmin/core/src/api/housing/housing-types';
import { checkHousingAvailability } from '@eliadmin/core/src/api/housing/housing-api';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Heading } from '@eliadmin/core/src/components/ui/heading';
import { Calendar } from '@eliadmin/core/src/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@eliadmin/core/src/components/ui/popover';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { IoIosArrowDown } from 'react-icons/io';
import { LuMinus, LuPlus } from 'react-icons/lu';
import { AiOutlineExclamationCircle } from 'react-icons/ai';

import {
  computeHousingNightlyPrice,
  formatSkDateRange,
  pluralGuests,
} from '@eliadmin/core/src/components/reservation/reservation-form/date-utils';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

function toDateKey(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function monthKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

const calendarClassNames = {
  day_button: 'h-8 w-8 p-0 font-normal text-foreground rounded-md hover:bg-muted cursor-pointer',
  selected:
    'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground rounded-md',
  outside: 'text-muted-foreground opacity-40',
  disabled: 'text-muted-foreground opacity-30 cursor-not-allowed pointer-events-none',
  today: 'bg-muted font-semibold',
  weekday: 'text-muted-foreground w-8 font-medium text-[0.75rem] text-center',
};

function addNights(date: Date, nights: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + nights);
  return result;
}

function isPeakSeason(date: Date): boolean {
  const month = date.getMonth();
  const day = date.getDate();
  return month === 6 || month === 7 || (month === 11 && day >= 24) || (month === 0 && day <= 3);
}

export function HousingInfoCard({ housing }: { housing: HousingPropertyDetail }) {
  const { t } = useTranslations();
  const locale = useLocaleStore(s => s.currentLocale);

  const router = useRouter();
  const [arrival, setArrival] = useState<Date | undefined>();
  const [departure, setDeparture] = useState<Date | undefined>();
  const [guests, setGuests] = useState(() => Math.min(2, housing.capacity_total || 2));
  const [dateOpen, setDateOpen] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [availabilityError, setAvailabilityError] = useState<string | null>(null);

  const fetchedMonthsRef = useRef<Set<string>>(new Set());
  const [availabilityDays, setAvailabilityDays] = useState<Map<string, HousingAvailabilityDay>>(
    new Map(),
  );

  const ensureMonthLoaded = useCallback(
    (date: Date) => {
      const key = monthKey(date);
      if (fetchedMonthsRef.current.has(key)) return;
      fetchedMonthsRef.current.add(key);

      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      const pad = (n: number) => String(n).padStart(2, '0');
      const dateFrom = `${year}-${pad(month)}-01`;
      const dateTo = `${year}-${pad(month)}-${pad(new Date(year, month, 0).getDate())}`;

      checkHousingAvailability({
        locale,
        body: { property: { id: housing.id }, date_from: dateFrom, date_to: dateTo },
      })
        .then(res => {
          setAvailabilityDays(prev => {
            const next = new Map(prev);
            for (const day of res.data.property.days) next.set(day.date, day);
            return next;
          });
        })
        .catch(() => {
          fetchedMonthsRef.current.delete(key);
        });
    },
    [housing.id, locale],
  );

  useEffect(() => {
    ensureMonthLoaded(new Date());
  }, [ensureMonthLoaded]);

  const isDayUnavailable = (date: Date) => {
    const day = availabilityDays.get(toDateKey(date));
    return !!day && (day.is_blocked || day.is_reserved || !day.is_available);
  };

  const nights =
    arrival && departure
      ? Math.round((departure.getTime() - arrival.getTime()) / (1000 * 60 * 60 * 24))
      : 0;

  const nightlyPrice = computeHousingNightlyPrice({
    propertyId: housing.id,
    guestsCount: guests,
    flatPricePerNight: Number(housing.price_per_night) || 0,
  });

  const pricePerPersonPerNight = nights > 0 ? Math.round(nightlyPrice / guests) : nightlyPrice;

  const peakSeason = arrival ? isPeakSeason(arrival) : false;
  const minNights = peakSeason ? 3 : 2;

  const dateLabel = arrival && departure ? formatSkDateRange(arrival, departure) : 'Vyberte termín';

  const handleVerify = async () => {
    setAvailabilityError(null);

    if (!arrival || !departure) {
      const params = new URLSearchParams();
      params.set('guests', String(guests));
      params.set('property', String(housing.id));
      router.push(`/rezervacia?${params.toString()}`);
      return;
    }

    const dateFrom = format(arrival, 'yyyy-MM-dd');
    const dateTo = format(departure, 'yyyy-MM-dd');

    setIsChecking(true);
    try {
      const res = await checkHousingAvailability({
        locale,
        body: { property: { id: housing.id }, date_from: dateFrom, date_to: dateTo },
      });

      if (!res.data.property.available) {
        setAvailabilityError(
          'Pre zvolený termín už toto ubytovanie nie je dostupné. Vyberte prosím iný termín.',
        );
        return;
      }

      const params = new URLSearchParams();
      params.set('arrival', dateFrom);
      params.set('departure', dateTo);
      params.set('guests', String(guests));
      params.set('property', String(housing.id));
      router.push(`/rezervacia?${params.toString()}`);
    } catch {
      setAvailabilityError('Overenie dostupnosti sa nepodarilo. Skúste to prosím znova.');
    } finally {
      setIsChecking(false);
    }
  };

  const data = [
    { label: 'Typ', value: housing?.rooms[0]?.housing_type?.name ?? '' },
    { label: 'Rozloha', value: `${housing?.area} m²` },
    { label: 'Počet izieb', value: housing?.rooms_count },
    { label: 'Kapacita ubytovania', value: housing?.capacity_total },
    { label: 'Zvieratá', value: housing?.allows_pets ? 'Povolené' : 'Zakázané' },
  ];

  const accomodationTax =
    locale === 'sk'
      ? `+ ${housing.id === 1 ? '1' : '2'}€ / os. / noc daň z ubytovania`
      : `+ ${housing.id === 1 ? '1' : '2'}€ / person / night accommodation tax`;

  return (
    <div
      id="date"
      className="flex w-full flex-col items-start justify-start gap-6 rounded-[24px] bg-[#F2EEE7] p-4 sm:p-6 lg:max-w-[486px]"
    >
      <div className="flex w-full flex-col items-start justify-start gap-2 sm:gap-3">
        <div className="flex items-baseline justify-start gap-2">
          <Heading tag="h3" title={`${pricePerPersonPerNight}€`} />
          <p className="text-base font-medium tracking-tight text-secondary sm:text-lg">/os./noc</p>
        </div>
        <p className="text-base font-normal text-secondary sm:text-lg">{accomodationTax}</p>
      </div>
      <div
        className={cn(
          'flex w-full flex-col items-center justify-center gap-4 rounded-[24px] bg-white p-3 sm:p-4',
          'housing-info-card__choosing-card-wrapper',
        )}
      >
        <div className="flex w-full flex-col items-center justify-center gap-2">
          {/* Date range picker */}
          <Popover open={dateOpen} onOpenChange={setDateOpen}>
            <PopoverTrigger asChild>
              <div
                className={cn(
                  'relative flex w-full flex-1 cursor-pointer items-center justify-start gap-3.5 px-6 py-3 md:px-6',
                )}
              >
                <div className="flex max-h-11 min-h-11 min-w-11 max-w-11 items-center justify-center rounded-full bg-[#F2EEE7]">
                  <Image
                    src={'/images/icons/search/date.svg'}
                    alt={t('text147')}
                    width={20}
                    height={20}
                    className="shrink-0 object-contain"
                  />
                </div>
                <div className="flex flex-col items-start justify-start gap-[2px]">
                  <p className="text-sm font-normal leading-[100%] text-secondary">
                    Príchod - odchod
                  </p>
                  <div className="flex items-center justify-start gap-[6px]">
                    <p
                      className={cn(
                        'text-base font-bold leading-[100%]',
                        arrival && departure ? 'text-secondary' : 'text-[#9E9E9E]',
                      )}
                    >
                      {dateLabel}
                    </p>
                    <IoIosArrowDown size={14} className="text-primary" />
                  </div>
                </div>
              </div>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-4" align="start">
              <div className="flex flex-col gap-4 sm:flex-row">
                <div>
                  <p className="mb-2 text-sm font-semibold text-secondary">Príchod</p>
                  <Calendar
                    mode="single"
                    selected={arrival}
                    onSelect={date => {
                      setArrival(date);
                      if (date && departure && departure < addNights(date, minNights)) {
                        setDeparture(undefined);
                      }
                    }}
                    onMonthChange={ensureMonthLoaded}
                    disabled={date =>
                      date < new Date(new Date().setHours(0, 0, 0, 0)) || isDayUnavailable(date)
                    }
                    classNames={calendarClassNames}
                  />
                </div>
                <div>
                  <p className="mb-2 text-sm font-semibold text-secondary">Odchod</p>
                  <Calendar
                    mode="single"
                    selected={departure}
                    onSelect={date => {
                      setDeparture(date);
                      if (date) setDateOpen(false);
                    }}
                    onMonthChange={ensureMonthLoaded}
                    disabled={date => {
                      const today = new Date(new Date().setHours(0, 0, 0, 0));
                      if (arrival ? date < addNights(arrival, minNights) : date < today) {
                        return true;
                      }
                      return isDayUnavailable(date);
                    }}
                    classNames={calendarClassNames}
                  />
                </div>
              </div>
            </PopoverContent>
          </Popover>

          <div className="h-[1px] w-full bg-[#DAD7D1]" />

          {/* Guest counter */}
          <div
            className={cn(
              'relative flex w-full flex-1 items-center justify-start gap-3.5 px-6 py-3 md:px-6',
            )}
          >
            <div className="flex max-h-11 min-h-11 min-w-11 max-w-11 items-center justify-center rounded-full bg-[#F2EEE7]">
              <Image
                src={'/images/icons/search/people.svg'}
                alt="Hostia"
                width={20}
                height={20}
                className="shrink-0 object-contain"
              />
            </div>
            <div className="flex w-full items-center justify-between">
              <div className="flex flex-col items-start justify-start gap-[2px]">
                <p className="text-sm font-normal leading-[100%] text-secondary">Hostia</p>
                <p className="text-base font-bold leading-[100%] text-secondary">
                  {pluralGuests(guests)}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  aria-label="Znížiť počet hostí"
                  onClick={() => setGuests(g => Math.max(1, g - 1))}
                  disabled={guests <= 1}
                  className="flex h-8 w-8 items-center justify-center rounded-full bg-[#F2EEE7] text-secondary transition-opacity disabled:opacity-30"
                >
                  <LuMinus size={14} />
                </button>
                <button
                  type="button"
                  aria-label="Zvýšiť počet hostí"
                  onClick={() => setGuests(g => Math.min(housing.capacity_total, g + 1))}
                  disabled={guests >= housing.capacity_total}
                  className="flex h-8 w-8 items-center justify-center rounded-full bg-[#F2EEE7] text-secondary transition-opacity disabled:opacity-30"
                >
                  <LuPlus size={14} />
                </button>
              </div>
            </div>
          </div>
        </div>
        <Button
          onClick={handleVerify}
          isLoading={isChecking}
          disabled={isChecking}
          className="w-full"
        >
          {t('text691')}
        </Button>
        {availabilityError && (
          <div className="flex w-full items-center justify-start gap-2 rounded-[16px] bg-[#FFD9CC] px-4 py-3">
            <AiOutlineExclamationCircle className="max-h-5 min-h-5 min-w-5 max-w-5 text-secondary" />
            <p className="text-left text-sm font-bold leading-[130%] text-secondary">
              {availabilityError}
            </p>
          </div>
        )}
      </div>
      <div className="flex w-full flex-col items-start justify-start gap-3">
        {data.map(row => (
          <div key={row.label} className="grid w-full grid-cols-2 gap-4 sm:gap-6">
            <p className="text-base font-medium tracking-tight text-secondary sm:text-lg">
              {row.label}
            </p>
            <p className="text-base font-bold tracking-tight text-secondary sm:text-lg">
              {row.value}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}
